import { useState } from "react";
import type { Language } from "../api";
import { useT } from "../i18n";
import { matchLanguages } from "./languages";

/**
 * LanguagePicker chooses a language by searching for it (ADR-0056).
 *
 * Every ISO 639-1 language is on the served list, which is far too many to
 * lay out and far too many for a dropdown a phone would open full-screen. So:
 * a search box, the common languages as chips until something is typed, and
 * the matches as chips after. The chosen language stays on screen as a
 * pressed chip whatever the search shows, so a query never hides the answer.
 */
export default function LanguagePicker({
  languages,
  value,
  onChoose,
  testID,
  unsetLabel,
  disabled = false,
}: {
  /** The served list. The client holds none of its own. */
  languages: Language[];
  /** The chosen code, or "" for none. */
  value: string;
  onChoose: (code: string) => void;
  /** Prefix of the test ids: `${testID}-search`, `${testID}-${code}`. */
  testID: string;
  /** Offers "not chosen" as a chip with this label, which chooses "". */
  unsetLabel?: string;
  disabled?: boolean;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const matches = matchLanguages(languages, query);
  // A stored code the list does not name still shows, as itself.
  const chosen =
    value === "" ? null : (languages.find((l) => l.code === value) ?? unlistedLanguage(value));
  const shown =
    chosen && !matches.some((l) => l.code === chosen.code) ? [chosen, ...matches] : matches;

  const chip = (pressed: boolean) =>
    `inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 ${
      pressed
        ? "border-sage bg-sage-soft font-medium text-sage-deep"
        : "border-line text-ink-soft hover:text-ink"
    }`;

  return (
    <div data-testid={testID}>
      <input
        type="search"
        dir="auto"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.common.searchLanguage}
        aria-label={t.common.searchLanguage}
        data-testid={`${testID}-search`}
        /* text-base: iOS zooms into any input under 16px on focus. */
        className="mb-2 block w-full rounded-control border border-line bg-surface px-3 py-2 text-base disabled:opacity-50"
      />
      <div className="flex flex-wrap gap-1.5">
        {unsetLabel && query.trim() === "" && (
          <button
            type="button"
            data-testid={`${testID}-unset`}
            aria-pressed={value === ""}
            disabled={disabled}
            onClick={() => onChoose("")}
            className={chip(value === "")}
          >
            {unsetLabel}
          </button>
        )}
        {shown.map((l) => (
          <button
            key={l.code}
            type="button"
            data-testid={`${testID}-${l.code}`}
            aria-pressed={l.code === value}
            disabled={disabled}
            onClick={() => onChoose(l.code)}
            className={chip(l.code === value)}
          >
            {/* Each name isolated on its own, and no direction on the chip:
                the endonym and the English name may be in different scripts. */}
            <bdi>{l.endonym}</bdi>
            {l.english !== l.endonym && (
              <bdi className="text-xs font-normal text-muted">{l.english}</bdi>
            )}
          </button>
        ))}
        {shown.length === 0 && (
          <p data-testid={`${testID}-none`} className="py-1.5 text-sm text-muted">
            {t.common.noLanguage}
          </p>
        )}
      </div>
    </div>
  );
}

function unlistedLanguage(code: string): Language {
  return { code, english: code, endonym: code, common: false };
}
